/**
 * Forms module.
 */

import emailMisspelled, { top100 } from 'email-misspelled';
import * as _validation from 'scripts/modules/_validation';
import {
	getDataAttr,
	hasDataAttr,
	getAttrOrData,
	hasAttrOrData,
} from 'scripts/helpers/_attributes';
import { handleSignup } from 'scripts/modules/_signup';
import { toCamelCase } from 'scripts/helpers/_strings';

const CUSTOM_ALLOWED_EMAIL_DOMAINS = [ 'freshbooks.com' ];

/**
 * Initialise all forms on a page.
 */
export const initForms = () => {
	const forms = getAllForms();

	if ( forms && 'forEach' in forms ) {
		forms.forEach( ( form ) => {
			if ( getDataAttr( form, 'form-handler' ) === 'handleSignup' ) {
				attachThreatMetrixScript();
			}

			form.addEventListener( 'submit', ( event ) => {
				event.preventDefault();
				event.stopPropagation();

				const fields = validateAllFields( form );
				const formValid = fields.length === fields.filter( ( field ) => field.isValid ).length;

				if ( formValid ) {
					form.classList.remove( 'was-validated' );
					const handler = getDataAttr( form, 'form-handler' );

					if ( handler === 'handleSignup' ) {
						handleSignup( form );
					} else if ( typeof handler === 'function' ) {
						handler.call( window, form );
					}
				} else {
					form.classList.add( 'was-validated' );
					const firstError = form.querySelector(
						'.form-control:invalid, .form-control-checkbox:invalid'
					);

					if ( firstError ) {
						firstError.focus();
					}
				}
			} );

			const fields = getAllFormFields( form );

			if ( Array.from( fields ).find( ( field ) => field.getAttribute( 'name' ) === 'email' ) ) {
				initiateEmailOptionalValidation( form );
			}

			fields.forEach( ( field ) => {
				// check if field is a Select to handle on change event
				if ( field.tagName === 'SELECT' ) {
					field.addEventListener( 'change', () => {
						validateField( field );
					} );
				} else {
					field.addEventListener( 'keyup', () => {
						validateField( field );
					} );
				}
			} );
		} );
	}
};

/**
 * Filter an array of DOM elements - by default filtering by 'type' attribute.
 *
 * @param {Array|NodeList} fields   Array or NodeList (array-like) of input field DOM elements.
 * @param {Array}          excludes Array of attribute values to filter.
 * @param {string}         attr     Attribute to filter on (defaults to 'type').
 */
export const filterInputFields = (
	fields,
	excludes = [ 'button', 'submit', 'reset', 'image' ],
	attr = 'type'
) => {
	return Array.from( fields ).filter( ( el ) => ! excludes.includes( el[ attr ] ) );
};

/**
 * Get all forms on a page (or in a container).
 *
 * @param {string|HTMLElement} [parent=document] A selector string or DOM element.
 */
export const getAllForms = ( parent ) => {
	let forms = [];

	if ( 'string' === typeof parent ) {
		parent = document.querySelector( parent );
	} else if ( ! parent ) {
		parent = document;
	}

	if ( parent && 'querySelectorAll' in parent ) {
		forms = parent.querySelectorAll( 'form' );
	}

	return forms;
};

/**
 * Get all fields within a form.
 *
 * @param {string|HTMLFormElement} form A selector string or DOM element.
 */
export const getAllFormFields = ( form ) => {
	let fields = [];

	if ( 'string' === typeof form ) {
		form = document.querySelector( form );
	}

	if ( form && form instanceof window.HTMLFormElement ) {
		if ( form && form.elements ) {
			fields = filterInputFields( form.elements );
		}
	}

	return fields;
};

/**
 * Get the type of a field.
 *
 * @param {HTMLElement} field A DOM element.
 */
export const getFieldType = ( field ) => {
	let fieldType = getDataAttr( field, 'type' );

	if ( fieldType ) {
		return fieldType;
	}

	if ( field.tagName ) {
		const tagName = field.tagName.toLowerCase();

		if ( tagName === 'input' && field.type ) {
			fieldType = field.type;
		} else {
			fieldType = tagName;
		}
	}

	return fieldType;
};

/**
 * Get the value of a field.
 *
 * @param {HTMLElement} field A DOM element.
 */
export const getFieldValue = ( field ) => {
	const fieldType = getFieldType( field );

	if ( 'select' === fieldType && field.multiple && field.selectedIndex >= 0 ) {
		const optArray = field.selectedOptions
			? Array.from( field.selectedOptions )
			: Array.from( field.options ).filter( ( opt ) => !! opt.selected );

		return optArray.map( ( opt ) => opt.value );
	}

	if ( 'checkbox' === fieldType || 'radio' === fieldType ) {
		return field.checked;
	}

	return field.value;
};

/**
 * Set the value of a field.
 *
 * @param {HTMLElement} field A DOM element.
 * @param {*}           value Value to set for the input field.
 * @return {HTMLElement} field element returned for chaining.
 */
export const setFieldValue = ( field, value ) => {
	const fieldType = getFieldType( field );

	if ( 'select' === fieldType && field.multiple && Array.isArray( value ) ) {
		field.value = null;
		Array.from( field.options )
			.filter( ( opt ) => value.includes( opt.value ) )
			.forEach( ( opt ) => {
				opt.selected = true;
			} );
	} else if ( 'checkbox' === fieldType || 'radio' === fieldType ) {
		field.checked = !! value;
	} else if ( undefined !== value ) {
		if ( value && field.maxLength && field.maxLength > 0 && value.length > field.maxLength ) {
			field.value = value.slice( 0, field.maxLength );
		} else {
			field.value = value;
		}
	}

	return field;
};

/**
 * Get the validation type of a field (data-validation).
 *
 * @param {HTMLElement} field Expects a DOM element
 * @return {Array} Types of validation required for this field.
 */
export const getValidationRules = ( field ) => {
	let validationRules = [];

	if ( field && field.dataset && field.dataset.validation ) {
		validationRules = field.dataset.validation.split( ' ' );
	} else {
		const fieldType = getFieldType( field );

		if ( fieldType === 'email' ) {
			validationRules.push( 'isEmail' );
		} else if ( fieldType === 'tel' ) {
			validationRules.push( 'isPhone' );
		}
	}

	if ( hasAttrOrData( field, 'required' ) ) {
		validationRules.push( 'required' );
	}

	return validationRules.map( ( rule ) => toCamelCase( rule ) );
};

/**
 * Validate a field.
 *
 * @param {HTMLElement} field Expects a DOM element.
 * @return {boolean} Field validity.
 */
export const validateField = ( field ) => {
	if ( ! field || ! field.validity ) {
		return false;
	}
	const validationRules = getValidationRules( field );
	const fieldVal = getFieldValue( field );
	const tooltip = field.parentNode ? field.parentNode.querySelector( '.invalid-tooltip' ) : false;
	const validationOptions = {
		allowZero: hasDataAttr( field, 'allow-zero' ),
		allowSpaces: hasDataAttr( field, 'allow-spaces' ),
	};

	field.setCustomValidity( '' ); // Reset validity for this field.
	if ( tooltip ) {
		tooltip.innerHTML = field.validationMessage;
	}

	// Check if field is required.
	if (
		validationRules.includes( 'required' ) &&
		_validation.isEmpty( fieldVal, validationOptions )
	) {
		field.setCustomValidity( 'This field is required' );
		if ( tooltip ) {
			tooltip.innerHTML = field.validationMessage;
		}
		return field.validity.valid;
	}

	if ( ! _validation.isEmpty( validationRules ) ) {
		// Check field type validation.
		validationRules.forEach( ( rule ) => {
			const notRule = rule.startsWith( 'not' ) && rule.replace( /^not([A-Z])/, 'is$1' );

			if ( typeof _validation[ rule ] === 'function' && ! _validation[ rule ]( fieldVal ) ) {
				field.setCustomValidity( 'This field is invalid' );
			} else if (
				typeof _validation[ notRule ] === 'function' &&
				_validation[ notRule ]( fieldVal )
			) {
				field.setCustomValidity( 'This field is invalid' );
			}

			if ( tooltip ) {
				tooltip.innerHTML = field.validationMessage;
			}
			if ( ! field.validity.valid ) {
				return field.validity.valid;
			}
		} );
	}

	// Check the length of the field value.
	const stringFieldVal = fieldVal.toString().trim();
	const minLength = getAttrOrData( field, 'minlength' ) || 0;
	const maxLength = getAttrOrData( field, 'maxlength' ) || window.Number.MAX_SAFE_INTEGER;

	if ( minLength > 0 || maxLength < window.Number.MAX_SAFE_INTEGER ) {
		if ( stringFieldVal.length < minLength ) {
			field.setCustomValidity( `Value is too short: minimum length is ${ minLength }` );
		} else if ( stringFieldVal.length > maxLength ) {
			field.setCustomValidity( `Value is too long: maximum length is ${ maxLength }` );
		}
		if ( tooltip ) {
			tooltip.innerHTML = field.validationMessage;
		}
	}

	// NOTE: Disabled until isInRange fixed.
	// if ( ( minLength && minLength > 0 ) || ( maxLength && maxLength > 0 ) ) {
	// 	const lengthValidation = _validation.isInRange(
	// 		stringFieldVal.length,
	// 		minLength,
	// 		maxLength
	// 	);

	// 	if ( minLength > 0 && lengthValidation < 0 ) {
	// 		msg = `Value is too short: minimum length is ${ minLength }`;
	// 		field.setCustomValidity( msg );
	// 	} else if ( maxLength < window.Number.MAX_SAFE_INTEGER && lengthValidation > 0 ) {
	// 		msg = `Value is too long: maximum length is ${ maxLength }`;
	// 		field.setCustomValidity( msg );
	// 	}
	// }

	return field.validity.valid;
};

/**
 * Validate all fields within a form.
 *
 * @param {string|HTMLFormElement} form A selector string or DOM element.
 * @return {Array} All fields and their validity.
 */
export const validateAllFields = ( form ) => {
	const fields = [];

	getAllFormFields( form ).forEach( ( el ) => {
		fields.push( {
			field: el,
			isValid: validateField( el ),
		} );
	} );

	return fields;
};

const initiateEmailOptionalValidation = ( signupForm ) => {
	const email = signupForm.querySelector( 'input[name="email"]' );
	const emailInfoBox = signupForm.querySelector( '#emailInfoBox' );
	const emailInfoBoxContent = emailInfoBox.querySelector( 'p' );
	const emailInfoBoxClose = emailInfoBox.querySelector( '.form-field-tooltip--close' );

	if ( email && emailInfoBox ) {
		let doCheck = false;
		const emailMisspelledChecker = emailMisspelled( {
			domains: [ ...top100, ...CUSTOM_ALLOWED_EMAIL_DOMAINS ],
		} );

		const checkIfEmailIsMisspelled = () => {
			const checkResult = emailMisspelledChecker( email.value );

			if ( checkResult.length > 0 ) {
				const suggestedEmail = checkResult[ 0 ].corrected;
				emailInfoBoxContent.innerHTML = `This email appears incorrect. Do you mean <button class="link" onclick="fillInEmail( event, '${ suggestedEmail }');">${ suggestedEmail }</button>?`;
				emailInfoBox.classList.add( 'form-field-tooltip--warning' );
				emailInfoBox.classList.remove( 'form-field-tooltip--info' );
				emailInfoBox.classList.remove( 'd-none' );
				email.classList.add( 'border-fb-warning' );

				attachCheckIfCloseTooltip();

				return true;
			}

			return false;
		};

		const checkIfEmailDomainIsValid = async () => {
			if ( email.value && email.value.includes( '@' ) && email.value.includes( '.' ) ) {
				// eslint-disable-next-line no-undef
				const data = new FormData();
				data.set( 'email', email.value );
				// eslint-disable-next-line no-undef
				const response = await fetch( '/wp-json/fp/v1/validate-email-domain', {
					method: 'POST',
					body: data,
				} );
				const result = await response.json();

				if ( result === false && doCheck === false ) {
					emailInfoBoxContent.innerText =
						'The email domain you’ve entered appears incorrect. Please review it once more before proceeding.';
					emailInfoBox.classList.add( 'form-field-tooltip--info' );
					emailInfoBox.classList.remove( 'form-field-tooltip--warning' );
					emailInfoBox.classList.remove( 'd-none' );
					email.classList.add( 'border-fb-info' );

					attachCheckIfCloseTooltip();
				}
			}
		};

		email.addEventListener( 'blur', async () => {
			if ( doCheck ) {
				doCheck = false;

				if ( ! checkIfEmailIsMisspelled() ) {
					await checkIfEmailDomainIsValid();
				}
			}
		} );

		email.addEventListener( 'keydown', ( event ) => {
			if ( event.key || event.keyCode ) {
				resetCheckProcess();
			}
		} );

		email.addEventListener( 'focus', () => {
			if ( ! doCheck ) {
				if ( emailInfoBox.classList.contains( 'form-field-tooltip--warning' ) ) {
					emailInfoBox.classList.remove( 'd-none' );
					email.classList.add( 'border-fb-warning' );
				} else if ( emailInfoBox.classList.contains( 'form-field-tooltip--info' ) ) {
					emailInfoBox.classList.remove( 'd-none' );
					email.classList.add( 'border-fb-info' );
				}
			}
		} );

		const hideTooltip = () => {
			emailInfoBox.classList.add( 'd-none' );
			email.classList.remove( 'border-fb-warning' );
			email.classList.remove( 'border-fb-info' );
		};

		const resetCheckProcess = () => {
			emailInfoBox.classList.add( 'd-none' );
			emailInfoBox.classList.remove( 'form-field-tooltip--warning' );
			emailInfoBox.classList.remove( 'form-field-tooltip--info' );
			email.classList.remove( 'border-fb-warning' );
			email.classList.remove( 'border-fb-info' );
			doCheck = true;

			window.document.removeEventListener( 'click', checkIfCloseTooltip );
		};

		const checkIfCloseTooltip = ( event ) => {
			if ( ! emailInfoBox.classList.contains( 'd-none' ) ) {
				if (
					event.target !== email &&
					event.target !== emailInfoBox &&
					! emailInfoBox.contains( event.target )
				) {
					hideTooltip();
				}
			}
		};

		const attachCheckIfCloseTooltip = () => {
			setTimeout( () => {
				window.document.removeEventListener( 'click', checkIfCloseTooltip );
				window.document.addEventListener( 'click', checkIfCloseTooltip );
			}, 100 );
		};

		signupForm.addEventListener( 'submit', resetCheckProcess );

		if ( emailInfoBoxClose ) {
			emailInfoBoxClose.addEventListener( 'click', resetCheckProcess );
		}

		if ( typeof window.fillInEmail === 'undefined' ) {
			window.fillInEmail = ( event, emailValue ) => {
				event.preventDefault();
				event.stopPropagation();

				email.value = emailValue;
				resetCheckProcess();
				return false;
			};
		}
	}
};

/**
 * Adds <script> node to <head> with JS function for Threat Metrix.
 * Copy-pasted from MagnumUI.
 *
 * @see https://github.com/freshbooks/magnum-ui/pull/15438/files#diff-6fb34a19dfea01d94dd9e7a538eadb3791c62656867b01984001d7278440d908
 */
const attachThreatMetrixScript = () => {
	if ( ! document.head.querySelector( '#tm-script' ) ) {
		const script = document.createElement( 'script' );
		script.setAttribute( 'id', 'tm-script' );
		script.setAttribute( 'type', 'text/javascript' );

		script.innerHTML = `
    (function(){
      var g=this||self;function z(){return"undefined"===typeof Date.now?(new Date).getTime():Date.now()}function N(E){this.L=E;16==this.L?(this.v=268435456,this.C=4026531839):(this.v=78364164096,this.C=2742745743359)}function l(E){return(Math.floor(Math.random()*E.C)+E.v).toString(E.L)};function T(E){this.C=E}T.prototype.supported=function(){return void 0!=window.localStorage};T.prototype.get=function(){return window.localStorage.getItem(this.C)};T.prototype.set=function(E){return window.localStorage.setItem(this.C,E)};T.prototype.set=T.prototype.set;function Z(){var E=z(),Y=new N(16);Y=l(Y)+l(Y)+l(Y)+l(Y);return[0,0,E,E,Y].join(":")}function J(){var E=new T("ed73f20edbf2b73");if(!E.supported())return null;E=E.get();if(null===E)return null;var Y=E.split("_");2===Y.length&&(E=Y[0]);return"0:"+E}
      function v(){var E=J();if(null===E)if(E=new T("ed73f20edbf2b74"),E.supported()){var Y=E.get();null===Y&&(Y=Z());var u=E.set;var S=Y.split(":");if(5!=S.length)S=Y;else{var t=parseInt(S[1],10)+1,K=z();K>parseInt(S[2],10)+157788E5?S=Z():(S[1]=t.toString(),S[3]=K,S=S.join(":"))}u.call(E,S);E="1:"+Y}else E=null;return E}
      function y(E,Y){E={iceServers:[{urls:"turn:aa.online-metrix.net?transport\x3dtcp",username:E,credential:Y},{urls:"turn:aa.online-metrix.net?transport\x3dudp",username:E,credential:Y}]};return"undefined"!==typeof window.RTCPeerConnection&&null!==window.RTCPeerConnection?new window.RTCPeerConnection(E):"undefined"!==typeof window.webkitRTCPeerConnection&&null!==window.webkitRTCPeerConnection?new window.webkitRTCPeerConnection(E):"undefined"!==typeof window.C&&null!==window.C?new window.C(E):null}
      function w(E,Y){var u=v();try{var S=y("2:"+E+":"+Y+":"+u,Y);if(S&&"undefined"!==typeof S.createDataChannel&&null!==S.createDataChannel){S.createDataChannel(Math.random().toString());var t=function(){};E=function(K){S.setLocalDescription(K,t,t)};"undefined"===typeof Promise||0<S.createOffer.length?S.createOffer(E,t):S.createOffer().then(E,t);setInterval(function(){"undefined"!==typeof S.close&&null!==S.close&&S.close();"undefined"!==typeof S.onicecandidate&&null!==S.onicecandidate&&(S.onicecandidate=
      function(){},S=null)},1E4)}}catch(K){}}var X=null;function h(E){for(var Y;null!==(Y=document.getElementById(E));)Y.parentElement.removeChild(Y)}
      function P(E,Y,u,S){if("undefined"!==typeof E&&"undefined"!==typeof Y&&"undefined"!==typeof u&&8===Y.length){if(-1!==u.indexOf(":"))throw Error("invalid session_id "+u);h("tdz_ifrm");h("tmx_tags_iframe");h("tmx_tags_js");var t=document;if("undefined"!==typeof t.currentScript&&null!==t.currentScript){var K=t.currentScript.getAttribute("nonce");"undefined"!==typeof K&&null!==K&&""!==K?X=K:"undefined"!==typeof t.currentScript.nonce&&null!==t.currentScript.nonce&&""!==t.currentScript.nonce&&
      (X=t.currentScript.nonce)}w(Y,u);t=document.getElementsByTagName("head").item(0);K=document.createElement("script");K.id="tmx_tags_js";K.setAttribute("type","text/javascript");var k=new N(36),I=Math.floor(Math.random()*k.C),n=Math.floor(Math.random()*k.C);n=(n-n%256+5+I)%k.C;var L=(885187064159+I)%k.C;I=l(k)+(I+k.v).toString(k.L);n=(L+k.v).toString(k.L)+(n+k.v).toString(k.L);E="https://"+E+"/"+I+".js";Y=[n+"\x3d"+Y,l(k)+l(k)+"\x3d"+u];"undefined"!==typeof S&&null!==S&&0<S.length&&Y.push(l(k)+l(k)+
      "\x3d"+S);K.setAttribute("src",E+"?"+Y.join("\x26"));null!==X&&(K.setAttribute("nonce",X),K.getAttribute("nonce")!==X&&(K.nonce=X));t.appendChild(K)}}var W=["profiling","send"],Q=g;W[0]in Q||"undefined"==typeof Q.execScript||Q.execScript("var "+W[0]);for(var R;W.length&&(R=W.shift());)W.length||void 0===P?Q[R]&&Q[R]!==Object.prototype[R]?Q=Q[R]:Q=Q[R]={}:Q[R]=P;}).call(this);
    `;

		document.head.appendChild( script );
	}
};
